import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'antd';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Chat from './Chat/ChatContainer';
import Notes from './Notes/NotesContainer';
import * as actions from '../../store/actions';
import * as streamActions from '../../store/actions/InterviewRecordingActions';
import NTInterviewVideoContainer from '../../components/Interview/NTInterviewVideoContainer.js';
import MeetingControlBar from '../../components/Meeting/MeetingControlBar.js';
import ScreenShareView from '../Interview/Meeting/ScreenShareView.js';
import { isInterviewDeveloper } from '../../helperFunctions';
import { useInterviewCommonLogic } from '../../hooks/useInterviewCommonLogic';

const DATA_MSG_TOPIC = 'interview-flow';

const INTRO_MESSAGE =
  "Hello! Welcome to this interview. My name is Neerja, and I'll be conducting your interview today. I'm an AI-powered interviewer. This interview consists of 10 questions. You'll have about 60 seconds to answer each one. Please try to be specific and share real examples from your experience wherever possible. Let's get started.";

const getIntroPrompt = candidateName =>
  `Before we begin with the questions, I'd like to get to know you a little. ${candidateName}, could you please give me a brief introduction about yourself — your background, your current role, and what brings you here today?`;

const getClosingMessage = candidateName =>
  `Thank you so much for your time today, ${candidateName}. That concludes our interview. We really appreciate you taking the time to speak with us. Our team will review your responses, and we'll get back to you soon with the next steps. We wish you all the best!`;

const QUESTION_PAUSE_MS = 60000;

const interviewQuestions = [
  'Could you walk me through your professional journey and what led you to pursue software engineering?',
  'What kind of projects or problems excite you the most as an engineer?',
  'Can you describe a technically challenging problem you solved recently and your approach to it?',
  'Tell me about a time you had to learn a new technology or framework quickly. How did you go about it?',
  'How do you approach code reviews — both giving and receiving feedback?',
  'Describe a situation where you disagreed with a teammate on a technical decision. How did you handle it?',
  'How do you decide what to work on first when you have multiple tasks with competing deadlines?',
  'Tell me about a time something you built did not go as planned in production. What happened and what did you learn?',
  'How do you ensure the quality and maintainability of your code over time?',
  'Where do you see your career heading in the next few years, and what kind of role or work are you looking for?',
];

async function sendToLiveTalking(sessionId, text) {
  try {
    const response = await fetch('https://livetalking-api-qa.codehall.in/human', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        text,
        type: 'echo',
        interrupt: true,
        sessionid: sessionId,
      }),
    });
    if (!response.ok) {
      console.error('Error sending to LiveTalking:', response.status);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Failed to send to LiveTalking:', error);
    return false;
  }
}

async function startInterviewFlow(sessionId, candidateName, waitForCandidate, sendSignal) {
  console.log('Waiting for candidate to join');
  await waitForCandidate();
  console.log('Candidate joined, starting interview flow');

  await new Promise(r => setTimeout(r, 5000));

  console.log('Sending AI introduction');
  sendSignal({ type: 'ai_speaking' });
  if (!(await sendToLiveTalking(sessionId, INTRO_MESSAGE))) {
    console.error('Failed to send introduction, stopping flow');
    return;
  }
  await new Promise(r => setTimeout(r, 15000));

  console.log('Asking candidate for introduction');
  if (!(await sendToLiveTalking(sessionId, getIntroPrompt(candidateName)))) {
    console.error('Failed to send intro prompt, stopping flow');
    return;
  }
  sendSignal({ type: 'answering', label: 'Your Introduction', duration: 60 });
  await new Promise(r => setTimeout(r, 60000));

  for (let i = 0; i < interviewQuestions.length; i++) {
    console.log(`Asking question ${i + 1} of ${interviewQuestions.length}`);
    const success = await sendToLiveTalking(sessionId, interviewQuestions[i]);
    if (!success) {
      console.error('Question failed, stopping sequence');
      return;
    }
    sendSignal({
      type: 'answering',
      label: `Question ${i + 1} of ${interviewQuestions.length}`,
      duration: 60,
    });
    console.log(`Question ${i + 1} of ${interviewQuestions.length} asked`);
    await new Promise(r => setTimeout(r, QUESTION_PAUSE_MS));
  }

  console.log('Sending closing message');
  sendSignal({ type: 'ai_speaking' });
  await sendToLiveTalking(sessionId, getClosingMessage(candidateName));
  await new Promise(r => setTimeout(r, 15000));
  sendSignal({ type: 'complete' });
  console.log('Interview flow completed');
}

// --- Countdown timer shown on candidate's screen ---

const InterviewTimer = ({ signal, t }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  const [label, setLabel] = useState('');
  const [phase, setPhase] = useState(null);

  useEffect(() => {
    if (!signal) {
      setPhase(null);
      return;
    }

    setPhase(signal.type);

    if (signal.type === 'answering') {
      setLabel(signal.label || '');
      setTimeLeft(signal.duration);
      const interval = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setTimeLeft(null);
      setLabel('');
    }
  }, [signal]);

  if (!phase) return null;

  const barBase = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 20px',
    background: 'rgba(0, 0, 0, 0.75)',
    backdropFilter: 'blur(4px)',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 500,
    minHeight: '44px',
  };

  if (phase === 'ai_speaking') {
    return (
      <div style={barBase}>
        <span>{t('nt_interview_container.please_listen')}</span>
      </div>
    );
  }

  if (phase === 'complete') {
    return (
      <div style={{ ...barBase, justifyContent: 'center' }}>
        <span>{t('nt_interview_container.interview_complete')}</span>
      </div>
    );
  }

  if (phase === 'answering' && timeLeft !== null) {
    const progress = signal.duration > 0 ? (timeLeft / signal.duration) * 100 : 0;
    const isLow = timeLeft <= 10;
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;

    return (
      <div style={barBase}>
        <span>{label}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '120px',
              height: '6px',
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '3px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${progress}%`,
                background: isLow ? '#ff4d4f' : '#52c41a',
                borderRadius: '3px',
                transition: 'width 1s linear, background 0.3s',
              }}
            />
          </div>
          <span
            style={{
              fontVariantNumeric: 'tabular-nums',
              minWidth: '36px',
              color: isLow ? '#ff4d4f' : '#fff',
              fontWeight: isLow ? 700 : 500,
            }}
          >
            {minutes}:{seconds.toString().padStart(2, '0')}
          </span>
        </div>
      </div>
    );
  }

  return null;
};

// --- Main container ---

const AIInterviewContainer = props => {
  const { t } = useTranslation('interview_container');
  const isDeveloper = isInterviewDeveloper(props.participants);

  const candidateJoinedRef = useRef(false);
  const candidateJoinedResolverRef = useRef(null);
  const meetingSessionRef = useRef(null);

  const [interviewSignal, setInterviewSignal] = useState(null);

  const {
    chat,
    currOption,
    setCurrOption,
    isWSConnected,
    isScreenShareView,
    curr_note,
    setCurrNoteState,
    inactivityModalVisible,
    countdown,
    contextHolder,
    sendMessage,
    toggleView,
    handleRecoveryModalOk,
    handleContinue,
    handleDisconnect,
    handleEndInterview,
  } = useInterviewCommonLogic(props);

  useEffect(() => {
    meetingSessionRef.current = props.meetingSession;
  }, [props.meetingSession]);

  // Developer: detect candidate joining
  useEffect(() => {
    if (!isDeveloper || candidateJoinedRef.current) return;

    const nonLeftAttendees = Object.values(props.attendees || {}).filter(a => !a?.hasLeft);
    if (nonLeftAttendees.length >= 2) {
      candidateJoinedRef.current = true;
      if (candidateJoinedResolverRef.current) {
        candidateJoinedResolverRef.current();
        candidateJoinedResolverRef.current = null;
      }
    }
  }, [props.attendees, isDeveloper]);

  // Developer: start interview flow and send signals via Chime data messages
  useEffect(() => {
    if (isDeveloper && props.webrtcSessionId) {
      console.log('WebRTC session ID received:', props.webrtcSessionId);
      const candidate = props.participants?.find(p => p.role === 'CA');
      const candidateName = candidate?.name || 'Candidate';

      const waitForCandidate = () =>
        new Promise(resolve => {
          if (candidateJoinedRef.current) {
            resolve();
          } else {
            candidateJoinedResolverRef.current = resolve;
          }
        });

      const sendSignal = payload => {
        const session = meetingSessionRef.current;
        if (session?.audioVideo) {
          try {
            session.audioVideo.realtimeSendDataMessage(DATA_MSG_TOPIC, JSON.stringify(payload));
          } catch (e) {
            console.error('Failed to send data message:', e);
          }
        }
      };

      startInterviewFlow(props.webrtcSessionId, candidateName, waitForCandidate, sendSignal);
    }
  }, [isDeveloper, props.webrtcSessionId]);

  // Candidate: subscribe to Chime data messages for timer signals
  useEffect(() => {
    if (isDeveloper || !props.meetingSession?.audioVideo) return;

    const handler = dataMessage => {
      try {
        const payload = JSON.parse(dataMessage.text());
        setInterviewSignal(payload);
      } catch (e) {
        console.error('Failed to parse interview signal:', e);
      }
    };

    props.meetingSession.audioVideo.realtimeSubscribeToReceiveDataMessage(DATA_MSG_TOPIC, handler);

    return () => {
      try {
        props.meetingSession.audioVideo.realtimeUnsubscribeFromReceiveDataMessage(DATA_MSG_TOPIC);
      } catch (e) {
        // session may already be torn down
      }
    };
  }, [props.meetingSession, isDeveloper]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 85px)',
        overflow: 'hidden',
      }}
    >
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {isScreenShareView ? (
          <ScreenShareView />
        ) : (
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'row',
              boxSizing: 'border-box',
              overflow: 'hidden',
              gap: '16px',
            }}
          >
            <div
              style={{
                flex: 1,
                background: '#202124',
                overflow: 'hidden',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 0,
                }}
              >
                <NTInterviewVideoContainer />
              </div>
              {!isDeveloper && (
                <InterviewTimer
                  signal={interviewSignal}
                  t={t}
                />
              )}
            </div>

            {(currOption === 'chat' || currOption === 'notes') && (
              <div
                style={{
                  width: '400px',
                  height: '100%',
                  background: 'transparent',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div style={{ padding: '16px', height: '100%', overflow: 'hidden' }}>
                  {currOption === 'chat' && (
                    <Chat
                      sendMessage={sendMessage}
                      chat={chat}
                      connected={isWSConnected}
                      chat_data={props.chat_data}
                      unreadCount={props.unreadCount}
                    />
                  )}
                  {currOption === 'notes' && (
                    <Notes
                      curr_note={curr_note}
                      setCurrNoteState={setCurrNoteState}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <MeetingControlBar
        setCurrOption={setCurrOption}
        isDeveloper={isInterviewDeveloper(props.participants)}
        onScreenShareClick={() => toggleView()}
        onEndMeeting={handleEndInterview}
        isScreenShareActive={isScreenShareView}
        currOption={currOption}
        interviewType={props.interview_type}
      />
      {contextHolder}
      {props.recoveryModalOpen && (
        <Modal
          title={t('nt_interview_container.screen_share_stopped_title')}
          open={props.recoveryModalOpen}
          onOk={handleRecoveryModalOk}
          closable={false}
          okText={t('nt_interview_container.share_screen_again')}
        >
          <p>{t('nt_interview_container.screen_share_stopped_message')}</p>
        </Modal>
      )}
      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            {t('nt_interview_container.interviewer_left_meeting_title')}
          </div>
        }
        open={inactivityModalVisible}
        onOk={handleContinue}
        onCancel={handleDisconnect}
        okText={t('nt_interview_container.continue_interview')}
        cancelText={t('nt_interview_container.end_interview')}
        closable={false}
        centered
      >
        <div style={{ textAlign: 'center' }}>
          <p>{t('nt_interview_container.interviewer_left_meeting_message')}</p>
          <p>
            {t('nt_interview_container.time_remaining')}: <strong>{countdown}</strong>{' '}
            {t('nt_interview_container.seconds')}
          </p>
        </div>
      </Modal>
    </div>
  );
};

const mapDispatchToProps = dispatch => {
  return {
    fetchInterviewInvite: (
      interview_id,
      hash_value,
      role,
      company_id,
      fetch_interview_data,
      callback,
    ) =>
      dispatch(
        actions.fetchInterviewInvite(
          interview_id,
          hash_value,
          role,
          company_id,
          fetch_interview_data,
          callback,
        ),
      ),
    fetchData: interview_id => dispatch(actions.fetchData(interview_id)),
    setEndInterviewUserDetail: user => dispatch(actions.setEndInterviewUserDetail(user)),
    setParticipants: participants => dispatch(actions.setParticipants(participants)),
    postNote: (data, callback) => dispatch(actions.postNote(data, callback)),
    checkNetworkConnection: () => dispatch(actions.checkNetworkConnection()),
    setHeartbeat: fire_time => dispatch(actions.setHeartbeat(fire_time)),
    setUnreadCount: count => dispatch(actions.setUnreadCount(count)),
    closeMediaRecoveryModal: () => dispatch(streamActions.closeMediaRecoveryModal()),
    initScreenStream: () => dispatch(streamActions.initScreenStream()),
    expireMeeting: (meetingId, isDeveloper) => {
      dispatch(actions.expireMeeting(meetingId, isDeveloper));
    },
  };
};

const mapStateToProps = state => {
  return {
    interview_id: state.interview.interview_id,
    interview_status: state.interview.status,
    interview_title: state.interview.title,
    interview_role: state.interview.role,
    interview_type: state.interview.interview_type,
    participants: state.interview.participants,
    start_time: state.interview.start_time,
    role: state.interview.role,
    user_id: state.auth.user_id,
    chat_data: state.events.chat_data,
    heartbeat: state.events.heartbeat,
    meetingSession: state.interviewRecording.meetingSession,
    unreadCount: state.events.unreadCount,
    recoveryModalOpen: state.interviewRecording.recoveryModalOpen,
    recoveryReason: state.interviewRecording.recoveryReason,
    meeting: state.meeting,
    webrtcSessionId: state.interviewRecording.webrtcSessionId,
    attendees: state.interviewRecording.attendees,
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AIInterviewContainer));
